/**
 * `/client-approvals`: how a client signs off on posts in PostDaily, and how
 * the same job works elsewhere.
 *
 * The comparison is the reason the page exists, so its rules match the
 * `/alternatives` pages (`comparison-data.ts`): every competitor fact comes
 * from that competitor's own pricing or help pages, each row carries the date
 * it was read, and PostDaily's own price is never written here. The route
 * reads it from the live catalog, and the plan that includes approvals comes
 * from `planConfigs`, so neither can drift from checkout.
 *
 * Never claim exclusivity. Sprout Social, Later and Metricool also let a
 * reviewer approve without an account; the honest difference is where on the
 * price ladder that starts, and what the approval actually does.
 */

import type { IsoDate } from "@/lib/content-date";
import type { CrossLink } from "./cross-links";

export type ApprovalRow = {
  tool: string;
  /** Plan where a client or reviewer can first approve posts. */
  plan: string;
  /** That plan's list price, as the vendor publishes it. */
  price: string;
  /** Whether the person approving needs an account with the vendor. */
  needsAccount: "yes" | "no";
  /** The detail that decides the comparison, in a phrase. */
  note: string;
  /** Where the facts came from. */
  source: string;
  checkedOn: IsoDate;
  /** `/alternatives/<slug>` when there is a comparison page. */
  compare?: string;
};

/**
 * Read from each vendor's own pages on the dates shown; see the research notes
 * behind `comparison-data.ts` for the quotes.
 */
export const APPROVAL_ROWS: readonly ApprovalRow[] = [
  {
    tool: "Sprout Social",
    plan: "Advanced",
    price: "$399 per seat a month, billed annually",
    needsAccount: "no",
    note: "External approval, up to 3 approvers",
    source: "sproutsocial.com/pricing and Sprout's help centre",
    checkedOn: "2026-09-27",
    compare: "sprout-social",
  },
  {
    tool: "Hootsuite",
    plan: "Advanced",
    price: "$399 per user a month, billed annually",
    needsAccount: "yes",
    note: "Approver is a member of your organisation; outside approvals can only be recorded, on Enterprise",
    source: "hootsuite.com/plans and Hootsuite's help centre",
    checkedOn: "2026-09-28",
    compare: "hootsuite",
  },
  {
    tool: "Metricool",
    plan: "Advanced",
    price: "From $53 a month, billed annually",
    needsAccount: "no",
    note: "Reviewers invited by email; a rejection stops the post",
    source: "metricool.com/pricing and Metricool's help centre",
    checkedOn: "2026-09-27",
    compare: "metricool",
  },
  {
    tool: "SocialBee",
    plan: "Accelerate",
    price: "$49 a month",
    needsAccount: "yes",
    note: "Reviewer creates a SocialBee account",
    source: "socialbee.com/pricing and SocialBee's help centre",
    checkedOn: "2026-09-27",
    compare: "socialbee",
  },
  {
    tool: "Later",
    plan: "Growth",
    price: "$37.50 a month, billed annually",
    needsAccount: "no",
    note: "Review links expire after 7 days, and approval does not block publishing",
    source: "later.com/pricing and Later's help centre",
    checkedOn: "2026-09-27",
    compare: "later",
  },
  {
    tool: "Planable",
    plan: "Basic (optional); Pro for required approval",
    price: "$33 a month, billed annually",
    needsAccount: "yes",
    note: "Client approves as an invited workspace member; public links can only view and comment",
    source: "planable.io/pricing and Planable's help centre",
    checkedOn: "2026-09-27",
    compare: "planable",
  },
  {
    tool: "Buffer",
    plan: "Team",
    price: "$10 per channel a month, billed annually",
    needsAccount: "yes",
    note: "Each reviewer has their own Buffer login",
    source: "buffer.com/pricing and Buffer's help centre",
    checkedOn: "2026-09-27",
    compare: "buffer",
  },
  {
    tool: "Publer",
    plan: "Business",
    price: "From $10 a month for one account",
    needsAccount: "yes",
    note: "Client is added as a member of your workspace",
    source: "publer.com/plans and Publer's help centre",
    checkedOn: "2026-09-27",
    compare: "publer",
  },
  {
    tool: "Meta Business Suite",
    plan: "None documented",
    price: "Free",
    needsAccount: "yes",
    note: "Access is shared through business portfolio permissions; Meta documents no approval step before publishing",
    source: "Meta's Business Help Centre",
    checkedOn: "2026-09-27",
    compare: "meta-business-suite",
  },
];

export type ApprovalStep = { heading: string; body: string };

/** How it works, in the order a user meets it. Every step is in the product. */
export const APPROVAL_STEPS: readonly ApprovalStep[] = [
  {
    heading: "Choose how strict to be",
    body: "In your workspace's approval settings, pick Optional, where you decide per post whether a client needs to see it, or Required, where every post you schedule is sent for review automatically. Off switches approvals away and releases anything that was waiting.",
  },
  {
    heading: "Create a review link and send it",
    body: "Make a link for the client and choose how long it lasts: 7, 30 or 90 days, or a year. Copy it into an email, a Slack message or anything else. Anyone with the link can review; nobody with it can edit, schedule or publish.",
  },
  {
    heading: "The client approves or asks for changes",
    body: "The link opens a review page in the browser with each post as it will look on its network. The client approves it, or requests changes with a comment, and adds their name so you know who decided. There is no account to create and no seat to pay for.",
  },
  {
    heading: "Nothing unapproved goes out",
    body: "While a post is waiting for review, or has changes requested, it is held: its scheduled time can pass and it will not publish. Once it is approved it goes out at its time, or within about five minutes if that time has already passed.",
  },
];

/** What happens after a decision, which is where approval tools differ most. */
export const APPROVAL_DETAILS: readonly ApprovalStep[] = [
  {
    heading: "Editing a post sends it back for review",
    body: "Each decision is stored against the version of the post the client saw. If you change the text, the media, the thread or the channels afterwards, the approval no longer applies and the post returns to waiting for review. Moving only its time does not, so rescheduling an approved post keeps it approved.",
  },
  {
    heading: "You can see who decided, and on which version",
    body: "The post's history lists each decision with the reviewer's name, their comment and the version they looked at, and marks the ones that came through a review link, so a question about who approved what has an answer.",
  },
  {
    heading: "Ending access is one click",
    body: "Rotate a link to issue a new one and break the old one at once, which is useful if it was forwarded too widely. Revoke it to switch it off entirely. Links also stop working on their own at the expiry you chose.",
  },
];

export type ApprovalFaq = { q: string; a: string };

export const APPROVAL_FAQS: readonly ApprovalFaq[] = [
  {
    q: "Does my client need a PostDaily account to approve posts?",
    a: "No. You send them a review link and they approve or request changes in the browser. They never sign up, and they do not take a seat on your plan.",
  },
  {
    q: "Will a post publish if the client has not approved it?",
    a: "No, not while approvals are turned on. A post waiting for review, or with changes requested, is held even if its scheduled time passes. When the client approves, it publishes at its time, or within about five minutes if that time has already gone.",
  },
  {
    q: "What happens if I edit a post after the client approved it?",
    a: "Changing the text, media, thread or channels sends it back for review, because the client approved a different version. Changing only the scheduled time keeps the approval.",
  },
  {
    q: "How long does a review link last?",
    a: "As long as you choose when you create it: 7, 30 or 90 days, or a year. You can rotate it to replace it, or revoke it to end access sooner.",
  },
  {
    q: "Is PostDaily the only scheduler with no-login client approval?",
    a: "No. Sprout Social, Later and Metricool also let a reviewer approve without an account. The difference is where it starts on the price ladder, how long links last, and whether an approval actually holds the post; the table on this page lays those out with the date each was checked.",
  },
];

export const APPROVAL_LINKS: readonly CrossLink[] = [
  { help: "client-approvals", label: "Set up client approval, step by step" },
  {
    blog: "client-approval-workflow-freelancers",
    label: "An approval workflow for freelancers with one client",
  },
  { to: "/pricing", label: "Plans and what each includes" },
  { to: "/alternatives", label: "Every comparison, with who each tool suits" },
];

/** The newest date any row was checked, for the table's caption. */
export function approvalsCheckedOn(rows: readonly ApprovalRow[]): IsoDate {
  return rows.reduce<IsoDate>(
    (latest, row) => (row.checkedOn > latest ? row.checkedOn : latest),
    rows[0]?.checkedOn ?? "2026-09-27",
  );
}
